import { useEffect, useMemo, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import PageLayout from "../components/PageLayout";
import RoundBox from "../components/RoundBox";
import {
  buttonStyle,
  buttonBackgroundColor,
  tanstackTableStyle,
  getTanstackThStyle,
  getTanstackTdStyle,
  tanstackTrStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  emptyRowStyle,
} from "../components/Style";
import Dropdown from "../components/Dropdown";
import TableInput from "../components/TableInput";
import Badge from "../components/Badge";
import { apiClient } from "../services/api";
import { Student } from "../types/Student";
import { GRADE_ORDER } from "../components/Grade";
import { isEucKrChar, filterEucKrChars, getEucKrByteLength } from "../utils/eucKrCharset";

const columnHelper = createColumnHelper<Student>();

interface RecipientTag {
  id: number;
  name: string;
  recipientLabel: string;
  phone: string;
  teacher: string;
  className: string;
}

// 카테고리별 특수문자
const SPECIAL_CHAR_GROUPS: { label: string; chars: string[] }[] = [
  { label: '문장 부호', chars: ['·','‥','…','―','〔','〕','〈','〉','《','》','「','」','『','』','【','】','§','※'] },
  { label: '화살표', chars: ['→','←','↑','↓','↔','↕','↗','↙','↖','↘'] },
  { label: '원문자', chars: ['㉠','㉡','㉢','㉣','㉤','㉥','㉦','㉧','㉨','㉩','㉪','㉫','㉬','㉭','㉮','㉯','㉰','㉱','㉲','㉳','㉴','㉵','㉶','㉷','㉸','㉹','㉺','㉻','ⓐ','ⓑ','ⓒ','ⓓ','ⓔ','ⓕ','ⓖ','ⓗ','ⓘ','ⓙ','ⓚ','ⓛ','ⓜ','ⓝ','ⓞ','ⓟ','ⓠ','ⓡ','ⓢ','ⓣ','ⓤ','ⓥ','ⓦ','ⓧ','ⓨ','ⓩ','①','②','③','④','⑤','⑥','⑦','⑧','⑨','⑩','⑪','⑫','⑬','⑭','⑮'] },
  { label: '수학 기호', chars: ['∴','∵','±','×','÷','≠','≤','≥','√','°','∠','⊥','≡','≒','∽','∞','∝','∫','∬','∮','∂','∇','⇒','⇔','∈','∋','⊆','⊇','⊂','⊃','∪','∩','∧','∨','￢','∀','∃','∑','∏','‰','¹','²','³','⁴','ⁿ','₁','₂','₃','₄'] },
  { label: '도형', chars: ['☆','★','○','●','◎','⊙','◇','◆','□','■','△','▲','▽','▼','◁','◀','▷','▶','♤','♠','♡','♥','♧','♣','◈','▣','◐','◑','▤','▥','▨','▧','▦','▩'] },
  { label: '그리스 문자', chars: ['Α','Β','Γ','Δ','Ε','Ζ','Η','Θ','Ι','Κ','Λ','Μ','Ν','Ξ','Ο','Π','Ρ','Σ','Τ','Υ','Φ','Χ','Ψ','Ω','α','β','γ','δ','ε','ζ','η','θ','ι','κ','λ','μ','ν','ξ','ο','π','ρ','σ','τ','υ','φ','χ','ψ','ω'] },
  { label: '단위', chars: ['㎕','㎖','㎗','ℓ','㎘','㏄','㎣','㎤','㎥','㎦','㎙','㎚','㎛','㎜','㎝','㎞','㎟','㎠','㎡','㎢','㏊','㎍','㎎','㎏','㏏','㎈','㎉','㏈','㎧','㎨','㎰','㎱','㎲','㎳','㎴','㎵','㎶','㎷','㎸','㎹','㎀','㎁','㎂','㎃','㎄','㎺','㎻','㎼','㎽','㎾','㎿','㎐','㎑','㎒','㎓','㎔','Ω','㏀','㏁','㎊','㎋','㎌','㏖','㏅','㎭','㎮','㎯','㏛','㎩','㎪','㎫','㎬','㏝','㏐','㏓','㏃','㏉','㏜','㏆'] },
  { label: '테두리', chars: ['─','│','┌','┐','┘','└','├','┬','┤','┴','┼','━','┃','┏','┓','┛','┗','┣','┳','┫','┻','╋','┠','┯','┨','┷','┿','┝','┰','┥','┸','╂','┒','┑','┚','┙','┖','┕','┎','┍','┞','┟','┡','┢','┦','┧','┩','┪','┭','┮','┱','┲','┵','┶','┹','┺','┽','┾','╀','╁','╃','╄','╅','╆','╇','╈','╉','╊'] },
  { label: '기타', chars: ['☏','☎','☜','☞','♭','♩','♪','♬','™','®'] },
];

export default function MessagePage() {
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [recipients, setRecipients] = useState<RecipientTag[]>([]);

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [testmode, setTestmode] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const [teacherFilter, setTeacherFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [specialCharsOpen, setSpecialCharsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const [selectedCategory, setSelectedCategory] = useState(0);
  const specialCharsButtonRef = useRef<HTMLButtonElement>(null);

  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);
  const [templatePopoverPos, setTemplatePopoverPos] = useState({ top: 0, left: 0 });
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templates, setTemplates] = useState<{ id: number; name: string; body: string }[]>([]);
  const templateBtnRef = useRef<HTMLButtonElement>(null);

  // 인수 입력 다이얼로그 (${...} 마커 처리)
  interface ArgSlot { componentName: string; value: string; }
  const [argsDialogOpen, setArgsDialogOpen] = useState(false);
  const [pendingTemplateBody, setPendingTemplateBody] = useState("");
  const [argSlots, setArgSlots] = useState<ArgSlot[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const innerHighlightRef = useRef<HTMLDivElement>(null);

  // textarea의 clientWidth(스크롤바 제외)를 오버레이 너비에 실시간 반영
  useEffect(() => {
    const ta = textareaRef.current;
    const hl = highlightRef.current;
    if (!ta || !hl) return;
    const syncWidth = () => { hl.style.width = ta.clientWidth + 'px'; };
    syncWidth();
    const obs = new ResizeObserver(syncWidth);
    obs.observe(ta);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    apiClient.getStudents().then(setStudents).catch(console.error);
  }, []);

  useEffect(() => {
    setClassFilter("");
  }, [teacherFilter]);

  const teacherOptions = useMemo(() => {
    const set = new Set<string>();
    students.forEach((s) => {
      const cls = (s.classes || []).find((c: any) => c.classCategory === "regular");
      if (cls?.teacher) set.add(cls.teacher);
    });
    return Array.from(set).sort();
  }, [students]);

  const classOptions = useMemo(() => {
    const set = new Set<string>();
    students.forEach((s) => {
      const cls = (s.classes || []).find((c: any) => c.classCategory === "regular");
      if (!cls) return;
      if (teacherFilter && cls.teacher !== teacherFilter) return;
      if (cls.className) set.add(cls.className);
    });
    return Array.from(set).sort();
  }, [students, teacherFilter]);

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const cls = (s.classes || []).find((c: any) => c.classCategory === "regular");
      if (teacherFilter && cls?.teacher !== teacherFilter) return false;
      if (classFilter && cls?.className !== classFilter) return false;
      return true;
    });
  }, [students, teacherFilter, classFilter]);

  const toggleStudent = (student: Student) => {
    const sid = student.id!;
    if (selectedIds.has(sid)) {
      setSelectedIds((prev) => { const s = new Set(prev); s.delete(sid); return s; });
      setRecipients((prev) => prev.filter((r) => r.id !== sid));
    } else {
      const phone = (student.messagePhone || "").replace(/-/g, "");
      if (!phone) return;
      const regularCls = (student.classes || []).find((c: any) => c.classCategory === "regular");
      setSelectedIds((prev) => new Set(prev).add(sid));
      setRecipients((prev) => [
        ...prev,
        {
          id: sid,
          name: student.name,
          recipientLabel: student.messageRecipient || "",
          phone: student.messagePhone || "",
          teacher: regularCls?.teacher || "",
          className: regularCls?.className || "",
        },
      ]);
    }
  };

  const removeRecipient = (id: number) => {
    setSelectedIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    setRecipients((prev) => prev.filter((r) => r.id !== id));
  };

  // ── 템플릿 인수 처리 헬퍼 ──────────────────────────────
  /** 정규식 이스케이프 */
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  /** #{월}, #{일} 등 정적 #{} 인수 자동 치환 (#{담당선생님} 등 수신자별 인수는 그대로 유지) */
  const resolveHashArgs = (body: string): string => {
    const now = new Date();
    return body
      .replace(/#{월}/g, `${now.getMonth() + 1}월`)
      .replace(/#{일}/g, `${now.getDate()}일`);
  };

  /** ${...} 인수 슬롯 파싱 (중복 제거) */
  const findDollarSlots = (body: string): ArgSlot[] => {
    const re = /\$\{([^}]+)\}/g;
    const seen = new Set<string>();
    const result: ArgSlot[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(body)) !== null) {
      if (!seen.has(m[1])) { seen.add(m[1]); result.push({ componentName: m[1], value: "" }); }
    }
    return result;
  };

  /** ${} 슬롯을 채워진 값으로 치환 후 message에 적용 */
  const commitArgs = () => {
    let result = pendingTemplateBody;
    for (const slot of argSlots) {
      result = result.replace(
        new RegExp(`\\$\\{${escapeRegExp(slot.componentName)}\\}`, "g"),
        slot.value
      );
    }
    setMessage(result);
    setArgsDialogOpen(false);
  };

  const updateArgSlot = (componentName: string, value: string) => {
    setArgSlots(prev => prev.map(s => s.componentName === componentName ? { ...s, value } : s));
  };
  // ────────────────────────────────────────────────────────

  const openTemplatePopover = async () => {
    setSelectedTemplateId(null);
    setTemplates([]);
    if (templateBtnRef.current) {
      const rect = templateBtnRef.current.getBoundingClientRect();
      const PW = 280;
      const PH = Math.min(60 + 3 * 68, window.innerHeight * 0.6);
      let left = rect.left;
      if (left + PW > window.innerWidth - 8) left = window.innerWidth - PW - 8;
      if (left < 8) left = 8;
      let top = rect.bottom + 4;
      if (top + PH > window.innerHeight - 8) top = rect.top - PH - 4;
      if (top < 8) top = 8;
      setTemplatePopoverPos({ top, left });
    }
    setTemplatePopoverOpen(true);
    try {
      const data = await apiClient.getMessageTemplates();
      setTemplates(data.map((t) => ({ id: t.id, name: t.templateName, body: t.templateContent })));
    } catch {
      setTemplates([]);
    }
  };

  const applyTemplate = () => {
    const t = templates.find((t) => t.id === selectedTemplateId);
    if (!t) return;
    setTemplatePopoverOpen(false);

    // 1) 정적 #{} 인수 자동 치환
    const resolved = resolveHashArgs(t.body);

    // 2) ${} 슬롯 확인
    const slots = findDollarSlots(resolved);
    if (slots.length === 0) {
      // 치환할 인수 없음 → 바로 적용
      setMessage(resolved);
      return;
    }
    // 인수 입력 다이얼로그 열기
    setPendingTemplateBody(resolved);
    setArgSlots(slots);
    setArgsDialogOpen(true);
  };

  const insertSpecialChar = (char: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? message.length;
    const end = ta.selectionEnd ?? message.length;
    const newMsg = message.slice(0, start) + char + message.slice(end);
    setMessage(newMsg);
    requestAnimationFrame(() => {
      ta.selectionStart = ta.selectionEnd = start + char.length;
      ta.focus();
    });
  };

  const POPOVER_W = 350;
  const POPOVER_H = 240; // 최소 예상 높이
  const toggleSpecialChars = () => {
    if (!specialCharsOpen && specialCharsButtonRef.current) {
      const rect = specialCharsButtonRef.current.getBoundingClientRect();
      // 메시지 박스(오른쪽 패널) 왼쪽 끝보다 더 왼쪽에 팝오버 배치
      let left = rect.left - POPOVER_W - 300;
      if (left < 8) left = 8;
      // 버튼 아래에서 시작하되 화면 아래로 잘리면 위로 올림
      let top = rect.bottom - 20;
      const maxTop = window.innerHeight - POPOVER_H - 8;
      if (top > maxTop) top = rect.top - POPOVER_H - 4;
      if (top < 8) top = 8;
      setPopoverPos({ top, left });
    }
    setSpecialCharsOpen((v) => !v);
  };

  const handleSend = async () => {
    if (recipients.length === 0 || !message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const buildRecipients = (group: RecipientTag[]) => group.map((r) => ({
        receiver: r.phone.replace(/-/g, ""),
        teacherName: r.teacher,
        className: r.className,
        studentName: r.name,
        recipientLabel: r.recipientLabel,
      }));

      let totalStripped = false;
      if (message.includes("#{담당선생님}")) {
        // 수신자별로 담당선생님 치환 후 그룹 전송
        const byTeacher = new Map<string, RecipientTag[]>();
        for (const r of recipients) {
          const key = r.teacher || "";
          if (!byTeacher.has(key)) byTeacher.set(key, []);
          byTeacher.get(key)!.push(r);
        }
        const entries = Array.from(byTeacher.entries());
        for (let ei = 0; ei < entries.length; ei++) {
          const teacher = entries[ei][0];
          const group = entries[ei][1];
          const personalized = message.replace(/#{담당선생님}/g, teacher);
          const filteredMsg = filterEucKrChars(personalized);
          if (filteredMsg !== personalized) totalStripped = true;
          await apiClient.sendMessageBulk(buildRecipients(group), filteredMsg, testmode);
        }
      } else {
        const filteredMsg = filterEucKrChars(message);
        if (filteredMsg !== message) totalStripped = true;
        await apiClient.sendMessageBulk(buildRecipients(recipients), filteredMsg, testmode);
      }

      setResult({ ok: true, text: `${recipients.length}명에게 메시지가 전송되었습니다.${totalStripped ? " (EUC-KR 미지원 문자 제거됨)" : ""}` });
      setMessage("");
    } catch (e: any) {
      const msg = e?.message || "전송 실패. 다시 시도해주세요.";
      setResult({ ok: false, text: msg });
    } finally {
      setSending(false);
    }
  };

  const canSend = recipients.length > 0 && message.trim().length > 0 && !sending;
  const filteredMessage = useMemo(() => filterEucKrChars(message), [message]);
  const byteLength = getEucKrByteLength(filteredMessage);
  const msgType = byteLength > 90 ? 'LMS' : 'SMS';
  const hasInvalidChars = filteredMessage !== message;
  const highlightSegments = useMemo(() => {
    const runs: { valid: boolean; text: string }[] = [];
    for (const char of message) {
      const valid = isEucKrChar(char);
      if (runs.length === 0 || runs[runs.length - 1].valid !== valid) {
        runs.push({ valid, text: char });
      } else {
        runs[runs.length - 1].text += char;
      }
    }
    return runs;
  }, [message]);

  const columns = useMemo(() => [
    columnHelper.display({
      id: "select",
      header: (props) => {
        const rows = props.table.getRowModel().rows;
        const eligible = rows.filter((r) => !!r.original.messagePhone);
        const allChecked = eligible.length > 0 && eligible.every((r) => selectedIds.has(r.original.id!));
        const handleToggleAll = () => {
          if (allChecked) {
            const ids = new Set(eligible.map((r) => r.original.id!));
            setSelectedIds((prev) => { const s = new Set(prev); ids.forEach((id) => s.delete(id)); return s; });
            setRecipients((prev) => prev.filter((r) => !ids.has(r.id)));
          } else {
            const toAdd: RecipientTag[] = [];
            eligible.forEach((r) => {
              const s = r.original;
              if (!selectedIds.has(s.id!)) {
                const regularCls = (s.classes || []).find((c: any) => c.classCategory === "regular");
                toAdd.push({
                  id: s.id!,
                  name: s.name,
                  recipientLabel: s.messageRecipient || "",
                  phone: s.messagePhone || "",
                  teacher: regularCls?.teacher || "",
                  className: regularCls?.className || "",
                });
              }
            });
            setSelectedIds((prev) => { const s = new Set(prev); toAdd.forEach((r) => s.add(r.id)); return s; });
            setRecipients((prev) => [...prev, ...toAdd]);
          }
        };
        return (
          <input
            type="checkbox"
            checked={allChecked}
            onChange={handleToggleAll}
            disabled={eligible.length === 0}
            style={{ cursor: eligible.length === 0 ? "not-allowed" : "pointer", width: 15, height: 15 }}
          />
        );
      },
      size: 40,
      cell: (info) => {
        const student = info.row.original;
        const disabled = !student.messagePhone;
        return (
          <input
            type="checkbox"
            checked={selectedIds.has(student.id!)}
            disabled={disabled}
            onChange={() => toggleStudent(student)}
            style={{ cursor: disabled ? "not-allowed" : "pointer", width: 15, height: 15 }}
          />
        );
      },
    }),
    columnHelper.display({
      id: "teacher",
      header: "선생님",
      size: 80,
      cell: (info) => {
        const cls = (info.row.original.classes || []).find((c: any) => c.classCategory === "regular");
        return <span style={{ fontSize: 13 }}>{cls?.teacher || "-"}</span>;
      },
    }),
    columnHelper.accessor("classes", {
      id: "className",
      header: "반",
      size: 120,
      cell: (info) => {
        const cls = (info.getValue() || []).find((c: any) => c.classCategory === "regular");
        return <span style={{ fontSize: 13 }}>{cls?.className || "-"}</span>;
      },
      sortingFn: (rowA, rowB) => {
        const a = (rowA.original.classes || []).find((c: any) => c.classCategory === "regular")?.className || "";
        const b = (rowB.original.classes || []).find((c: any) => c.classCategory === "regular")?.className || "";
        return a.localeCompare(b);
      },
    }),
    columnHelper.accessor("name", {
      header: "이름",
      size: 80,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue()}</span>,
      sortingFn: (rowA, rowB, colId) => {
        const ga = GRADE_ORDER[rowA.original.grade] || 999;
        const gb = GRADE_ORDER[rowB.original.grade] || 999;
        if (ga !== gb) return ga - gb;
        return rowA.getValue<string>(colId).localeCompare(rowB.getValue<string>(colId));
      },
    }),
    columnHelper.accessor("messageRecipient", {
      header: "메시지 수신자",
      size: 70,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue() || "-"}</span>,
    }),
    columnHelper.accessor("messagePhone", {
      header: "수신 번호",
      size: 120,
      cell: (info) => <span style={{ fontSize: 13 }}>{info.getValue() || "-"}</span>,
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [selectedIds]);

  const table = useReactTable({
    data: filteredStudents,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout title="메시지 보내기">
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* 왼쪽: 학생 테이블 (2/3) */}
        <div style={{ flex: 2, minWidth: 0 }}>
          <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
            <table style={tanstackTableStyle}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th key={header.id} style={getTanstackThStyle(false, header.getSize())}>
                        {header.isPlaceholder ? null : (
                          <div>
                            {header.column.getCanSort() ? (
                              <div onClick={header.column.getToggleSortingHandler()} style={sortHeaderStyle}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{ asc: <span style={sortArrowStyle}>▲</span>, desc: <span style={sortArrowStyle}>▼</span> }[header.column.getIsSorted() as string] ?? null}
                              </div>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                            <div style={filterContainerStyle}>
                              {header.column.id === "teacher" && (
                                <Dropdown
                                  value={teacherFilter}
                                  onChange={(value) => setTeacherFilter(value)}
                                  options={[
                                    { value: "", label: "전체" },
                                    ...teacherOptions.map((t) => ({ value: t, label: t })),
                                  ]}
                                />
                              )}
                              {header.column.id === "className" && (
                                <Dropdown
                                  value={classFilter}
                                  onChange={(value) => setClassFilter(value)}
                                  options={[
                                    { value: "", label: "전체" },
                                    ...classOptions.map((c) => ({ value: c, label: c })),
                                  ]}
                                />
                              )}
                              {header.column.id === "name" && (
                                <TableInput
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(e) => header.column.setFilterValue(e.target.value)}
                                  placeholder="검색"
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} style={emptyRowStyle}>학생이 없습니다.</td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      style={{
                        ...tanstackTrStyle,
                        backgroundColor: selectedIds.has(row.original.id!) ? "#f0f4ff" : undefined,
                        cursor: row.original.messagePhone ? "pointer" : "default",
                      }}
                      onClick={() => toggleStudent(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{ ...getTanstackTdStyle(false, cell.column.getSize()), textAlign: "center" }}
                          onClick={(e) => { if (cell.column.id === "select") e.stopPropagation(); }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 오른쪽: 메시지 전송 (1/3) - sticky */}
        <div style={{ flex: 1, minWidth: 280, position: "sticky", top: 16 }}>
          <RoundBox label="메시지 보내기">
            <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>

              {/* 메시지 수신자 태그 목록 */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6 }}>
                  수신자 ({recipients.length}명)
                </div>
                <div style={{
                  height: 96,
                  overflowY: "auto",
                  border: "1px solid #d0d7de",
                  borderRadius: 6,
                  padding: "6px 8px",
                  display: "flex",
                  flexWrap: "wrap",
                  alignContent: "flex-start",
                  alignItems: "flex-start",
                  gap: 6,
                  backgroundColor: "#fafafa",
                }}>
                  {recipients.length === 0 ? (
                    <span style={{ fontSize: 13, color: "#aaa", lineHeight: "32px" }}>
                      왼쪽 테이블에서 학생을 선택하세요.
                    </span>
                  ) : (
                    recipients.map((r) => (
                      <span
                        key={r.id}
                        style={{
                          position: "relative",
                          display: "inline-flex",
                          alignItems: "center",
                          alignSelf: "flex-start",
                          gap: 4,
                          padding: "3px 14px 3px 8px",
                          height: 24,
                          boxSizing: "border-box",
                          borderRadius: 12,
                          backgroundColor: "#e3efff",
                          border: "1px solid #b6d4fe",
                          fontSize: 12,
                          color: "#1a56a8",
                          fontWeight: 500,
                          lineHeight: 1.4,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span>{r.name}</span>
                        {r.recipientLabel && (
                          <span style={{ opacity: 0.7 }}>({r.recipientLabel})</span>
                        )}
                        <span
                          onClick={(e) => { e.stopPropagation(); removeRecipient(r.id); }}
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: "#555",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            lineHeight: 1,
                            cursor: "pointer",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          ×
                        </span>
                      </span>
                    ))
                  )}
                </div>
              </div>

              {/* 메시지 본문 */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: "#444" }}>
                    메시지 내용
                  </label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      ref={templateBtnRef}
                      type="button"
                      onClick={openTemplatePopover}
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        border: "1px solid #d0d7de",
                        borderRadius: 4,
                        backgroundColor: "#fafafa",
                        color: "#555",
                        cursor: "pointer",
                        fontWeight: 500,
                        lineHeight: 1.6,
                      }}
                    >
                      템플릿 적용
                    </button>
                    <button
                      ref={specialCharsButtonRef}
                      type="button"
                      onClick={toggleSpecialChars}
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        border: "1px solid #d0d7de",
                        borderRadius: 4,
                        backgroundColor: specialCharsOpen ? "#e3efff" : "#fafafa",
                        color: specialCharsOpen ? "#1a56a8" : "#555",
                        cursor: "pointer",
                        fontWeight: 500,
                        lineHeight: 1.6,
                      }}
                    >
                      특수문자 도구 {specialCharsOpen ? "닫기" : "열기"}
                    </button>
                  </div>
                </div>
                <div style={{ position: "relative", width: "100%", boxSizing: "border-box" }}>
                  {/* EUC-KR 미지원 문자 하이라이트 레이어 */}
                  <div
                    aria-hidden="true"
                    ref={highlightRef}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      padding: "8px 10px",
                      fontSize: 14,
                      lineHeight: 1.6,
                      fontFamily: "inherit",
                      overflow: "hidden",
                      pointerEvents: "none",
                      zIndex: 3,
                      color: "transparent",
                      boxSizing: "border-box",
                    }}
                  >
                    <div ref={innerHighlightRef} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word" }}>
                      {highlightSegments.map((run, i) =>
                        run.valid ? (
                          <span key={i}>{run.text}</span>
                        ) : (
                          <span
                            key={i}
                            style={{
                              backgroundColor: "rgba(220,38,38,0.2)",
                              borderBottom: "2px solid #dc2626",
                              borderRadius: 2,
                            }}
                          >
                            {run.text}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onScroll={() => {
                      const ta = textareaRef.current;
                      const inner = innerHighlightRef.current;
                      const hl = highlightRef.current;
                      if (ta && inner && hl) {
                        hl.style.width = ta.clientWidth + 'px';
                        inner.style.transform = `translateY(-${ta.scrollTop}px)`;
                      }
                    }}
                    placeholder="전송할 메시지를 입력하세요."
                    rows={7}
                    style={{
                      position: "relative",
                      zIndex: 2,
                      width: "100%",
                      boxSizing: "border-box",
                      border: "1px solid #d0d7de",
                      borderRadius: 6,
                      padding: "8px 10px",
                      fontSize: 14,
                      color: "#222",
                      resize: "vertical",
                      outline: "none",
                      fontFamily: "inherit",
                      lineHeight: 1.6,
                      backgroundColor: "#fff",
                      caretColor: "#222",
                    }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, color: "#666" }}>메시지 형식:</span>
                    <Badge variant={msgType === 'SMS' ? 'primary' : 'warning'}>{msgType}</Badge>
                    {hasInvalidChars && (
                      <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
                        ⚠ 미지원 문자 포함
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: "#888" }}>{byteLength}바이트</span>
                </div>
              </div>

              {/* 결과 메시지 */}
              {result && (
                <div style={{
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  backgroundColor: result.ok ? "#e6ffed" : "#fff0f0",
                  color: result.ok ? "#1a7f37" : "#cf222e",
                  border: `1px solid ${result.ok ? "#b7f5c2" : "#ffcdd2"}`,
                }}>
                  {result.text}
                </div>
              )}

              {/* 테스트 모드 + 전송 버튼 */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#666", cursor: "pointer", userSelect: "none" }}>
                  <input
                    type="checkbox"
                    checked={testmode}
                    onChange={(e) => setTestmode(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  테스트 모드
                </label>
                <button
                  onClick={handleSend}
                  disabled={!canSend}
                  style={{
                    ...buttonStyle,
                    ...buttonBackgroundColor.save,
                    opacity: canSend ? 1 : 0.5,
                    cursor: canSend ? "pointer" : "not-allowed",
                    padding: "8px 20px",
                    fontSize: 14,
                  }}
                >
                  {sending ? "전송 중..." : testmode ? "테스트 전송" : "전송"}
                </button>
              </div>
            </div>
          </RoundBox>
        </div>
      </div>

      {/* 인수 입력 다이얼로그 (${...} 마커 처리) */}
      {argsDialogOpen && (
        <div
          style={{
            position: "fixed", inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1100,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setArgsDialogOpen(false); }}
        >
          <div style={{
            backgroundColor: "#fff",
            borderRadius: 10,
            boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
            padding: "20px 24px",
            width: 420,
            maxWidth: "90vw",
            maxHeight: "80vh",
            overflowY: "auto",
            boxSizing: "border-box",
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#222", marginBottom: 16 }}>
              인수 입력
            </div>
            {argSlots.map((slot) => (
              <div key={slot.componentName} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6 }}>
                  {slot.componentName === "Unordered List 자동 생성 컴포넌트"
                    ? "목록 입력"
                    : slot.componentName}
                </div>
                {slot.componentName === "Unordered List 자동 생성 컴포넌트" ? (
                  <div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                      한 줄에 하나씩 입력하세요. 각 줄은 자동으로 ― 항목으로 변환됩니다.
                    </div>
                    <textarea
                      rows={5}
                      placeholder={"항목 1\n항목 2\n항목 3"}
                      value={slot.value.replace(/^― /gm, "")}
                      onChange={(e) => {
                        const lines = e.target.value
                          .split("\n")
                          .map(l => l.trim() === "" ? "" : `― ${l.trimStart().replace(/^― /, "")}`);
                        updateArgSlot(slot.componentName, lines.join("\n"));
                      }}
                      style={{
                        width: "100%", boxSizing: "border-box",
                        fontSize: 13, padding: "8px 10px",
                        border: "1px solid #d0d7de", borderRadius: 6,
                        resize: "vertical", fontFamily: "inherit",
                      }}
                    />
                    {slot.value && (
                      <div style={{
                        marginTop: 6, padding: "6px 10px",
                        backgroundColor: "#f6f8fa",
                        border: "1px solid #e4e8ec",
                        borderRadius: 6, fontSize: 12, color: "#555",
                        whiteSpace: "pre-wrap",
                      }}>
                        {slot.value}
                      </div>
                    )}
                  </div>
                ) : (
                  <textarea
                    rows={3}
                    placeholder={`${slot.componentName} 내용을 입력하세요`}
                    value={slot.value}
                    onChange={(e) => updateArgSlot(slot.componentName, e.target.value)}
                    style={{
                      width: "100%", boxSizing: "border-box",
                      fontSize: 13, padding: "8px 10px",
                      border: "1px solid #d0d7de", borderRadius: 6,
                      resize: "vertical", fontFamily: "inherit",
                    }}
                  />
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setArgsDialogOpen(false)}
                style={{
                  fontSize: 13, padding: "6px 14px",
                  border: "1px solid #d0d7de", borderRadius: 6,
                  backgroundColor: "#fafafa", color: "#555", cursor: "pointer",
                }}
              >
                취소
              </button>
              <button
                type="button"
                onClick={commitArgs}
                style={{
                  fontSize: 13, padding: "6px 14px",
                  border: "none", borderRadius: 6,
                  backgroundColor: "#0969da", color: "#fff",
                  cursor: "pointer", fontWeight: 600,
                }}
              >
                적용
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 템플릿 적용 팝오버 */}
      {templatePopoverOpen && (
        <div
          style={{
            position: "fixed",
            top: templatePopoverPos.top,
            left: templatePopoverPos.left,
            width: 280,
            backgroundColor: "#fff",
            border: "1px solid #d0d7de",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
            padding: "12px 14px",
            zIndex: 1001,
            boxSizing: "border-box",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 10 }}>
            템플릿 선택
          </div>
          {templates.length === 0 ? (
            <div style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>
              등록된 템플릿이 없습니다.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {templates.map((t) => (
                <label
                  key={t.id}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    cursor: "pointer",
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: `1px solid ${selectedTemplateId === t.id ? "#0969da" : "#e4e8ec"}`,
                    backgroundColor: selectedTemplateId === t.id ? "#f0f6ff" : "#fafafa",
                    fontSize: 13,
                    color: "#222",
                  }}
                >
                  <input
                    type="radio"
                    name="template-select"
                    value={t.id}
                    checked={selectedTemplateId === t.id}
                    onChange={() => setSelectedTemplateId(t.id)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div style={{
                      fontSize: 11,
                      color: "#888",
                      marginTop: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                    }}>
                      {t.body.split("\n")[0] || "(내용 없음)"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => setTemplatePopoverOpen(false)}
              style={{
                fontSize: 12,
                padding: "4px 10px",
                border: "1px solid #d0d7de",
                borderRadius: 4,
                backgroundColor: "#fafafa",
                color: "#555",
                cursor: "pointer",
              }}
            >
              취소
            </button>
            <button
              type="button"
              onClick={applyTemplate}
              disabled={!selectedTemplateId}
              style={{
                fontSize: 12,
                padding: "4px 10px",
                border: "none",
                borderRadius: 4,
                backgroundColor: selectedTemplateId ? "#0969da" : "#ccc",
                color: "#fff",
                cursor: selectedTemplateId ? "pointer" : "not-allowed",
                fontWeight: 600,
              }}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 특수문자 도구 팝오버 */}
      {specialCharsOpen && (
        <div
          style={{
            position: "fixed",
            top: popoverPos.top,
            left: popoverPos.left,
            width: POPOVER_W,
            backgroundColor: "#fff",
            border: "1px solid #d0d7de",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
            padding: "10px 12px",
            zIndex: 1000,
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>특수문자 도구</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(Number(e.target.value))}
              style={{
                fontSize: 12,
                border: "1px solid #d0d7de",
                borderRadius: 4,
                padding: "2px 4px",
                backgroundColor: "#fafafa",
                color: "#333",
                cursor: "pointer",
              }}
            >
              {SPECIAL_CHAR_GROUPS.map((g, i) => (
                <option key={i} value={i}>{g.label}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {SPECIAL_CHAR_GROUPS[selectedCategory].chars.map((char, i) => (
              <button
                key={i}
                type="button"
                onClick={() => insertSpecialChar(char)}
                title={`U+${char.codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0')}`}
                style={{
                  width: 28,
                  height: 28,
                  border: "1px solid #d0d7de",
                  borderRadius: 4,
                  backgroundColor: "#fafafa",
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                {char}
              </button>
            ))}
          </div>
        </div>
      )}
    </PageLayout>
  );
}
